import React, { useEffect, useState } from "react";
import { useContent } from "../../hooks/useContent";
import { useLanguage } from "../../contexts/LanguageContext";
import type { ContactRequestContentProps } from "../../content/component-contact-request";
import { Modal } from "../ui/Modal";
import { ContactRequestForm } from "./ContactRequestForm";

export type ContactRequestModalProps = {
    isOpen: boolean;
    onClose: () => void;
    /** Vorbelegtes Thema aus der aufrufenden Sektion (Entscheid C5). */
    topic: string;
    recipientEmail?: string;
    recipientName?: string;
    /** Wessen Kalender die freien Zeiten liefert (Entscheid C5). */
    calendarId?: string;
    /**
     * Kennung der Kampagnenseite, z.B. `ausschreibungen`. Das Formular meldet
     * sie als `campaign_page` an der Conversion `contact_request` (QUAWEB-42).
     */
    campaignPage?: string;
};

/**
 * Das globale Kontakt-Modal — ersetzt den Google-Kalenderlink (Endgame v0.3 § 1).
 *
 * Der Zustand «abgeschickt» lebt hier und nicht im Formular, damit das Modal
 * nach dem Absenden die Danke-Ansicht zeigen kann, ohne das Formular am Leben
 * zu halten.
 */
export const ContactRequestModal: React.FC<ContactRequestModalProps> = ({
    isOpen,
    onClose,
    topic,
    recipientEmail,
    recipientName,
    calendarId,
    campaignPage,
}) => {
    const { getComponentContent, getSectionContent } = useContent();
    const { language } = useLanguage();
    const content = getComponentContent<ContactRequestContentProps>("contactRequest");
    const [isSubmitted, setIsSubmitted] = useState(false);

    /*
     * Rueckfall ist die GLOBALE KONTAKT-SEKTION, nicht eine Umgebungsvariable.
     *
     * Ruft eine Sektion ohne eigenen Ansprechpartner das Modal auf — etwa die
     * `ClosingCtaSection`, die keine `person` fuehrt —, gilt derselbe Kontakt,
     * den die Website ueberall sonst zeigt. Vorher lag dieser Rueckfall in
     * `GOOGLE_CALENDAR_ID` bzw. `INTERNAL_MAIL_RECIPIENT`: zwei Mechanismen
     * fuer dieselbe Frage, und der eine liess sich nicht aus dem Content
     * ablesen. Wer den Ansprechpartner wechselt, aendert jetzt eine Stelle.
     */
    const globalContact = getSectionContent<{
        person?: { name: string; email: string };
    }>("contact");
    const fallbackPerson = globalContact?.person;

    // Beim erneuten Oeffnen wieder das Formular zeigen, nicht die Danke-Ansicht
    // des letzten Mals.
    useEffect(() => {
        if (isOpen) {
            setIsSubmitted(false);
        }
    }, [isOpen]);

    // Ohne Inhalt kein Modal: ein leerer Dialog waere schlimmer als der
    // urspruengliche Kalenderlink.
    if (!content) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isSubmitted ? content.thankYouTitle : content.title}>
            {isSubmitted ? (
                <div className="space-y-6">
                    <p className="text-gray-700">{content.thankYouMessage}</p>
                    <button
                        type="button"
                        onClick={onClose}
                        // eslint-disable-next-line max-len
                        className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-dark"
                    >
                        {content.close}
                    </button>
                </div>
            ) : (
                <ContactRequestForm
                    content={content}
                    topic={topic}
                    recipientEmail={recipientEmail ?? fallbackPerson?.email}
                    recipientName={recipientName ?? fallbackPerson?.name}
                    calendarId={calendarId ?? fallbackPerson?.email}
                    campaignPage={campaignPage}
                    language={language === "en" ? "en" : "de"}
                    onSuccess={() => setIsSubmitted(true)}
                    onCancel={onClose}
                />
            )}
        </Modal>
    );
};
